<template>
  <div
    ref="searchContainerRef"
    :class="[isOverlayDetailPage ? 'absolute top-0 start-0 end-0 z-50' : 'sticky top-0 z-50', 'w-screen max-w-[100vw]']"
  >
    <nav
      class="grid grid-cols-[1fr_auto_1fr] items-center px-5 h-[56px]"
      :class="[isOverlayDetailPage ? 'bg-gradient-to-b from-black/60 to-transparent' : 'bg-gold-primary-10']"
    >
      <div class="relative flex items-center justify-self-start">
        <div
          v-if="isPortfolioPage && isSearchExpanded"
          class="cursor-pointer flex items-center justify-center w-10 h-10 rounded-[12px] bg-white border-base border-black-20"
          @click="handleClosePortfolioSearch"
        >
          <UIIcon
            name="chevron-big-left"
            class="!size-6 text-black-100"
          />
        </div>
        <div
          v-else-if="isPortfolioPage && !isSearchExpanded"
          class="cursor-pointer flex items-center justify-center w-10 h-10 rounded-[12px] bg-white border-base border-black-20"
          @click="handleContextualIconClick"
        >
          <UIIcon
            name="search"
            class="!size-6 text-black-100"
          />
        </div>
        <div
          v-else-if="isSearchExpanded && !isPortfolioPage"
          class="absolute start-0 z-20 flex items-center bg-white rounded-lg border border-black-20 px-3 py-2 w-[250px] transition-all duration-300"
        >
          <UIIcon
            name="search"
            class="text-black-60 me-2"
          />
          <input
            ref="searchInputRef"
            v-model="searchQuery"
            type="text"
            :placeholder="t('common.actions.search')"
            class="flex-1 border-none outline-none text-sm"
            @keydown.enter="performSearch"
            @blur="handleSearchBlur"
          />
          <UIBaseTransition>
            <div
              v-if="isSearchLoading"
              class="flex items-center justify-center ms-2"
            >
              <UIProgressSpinner
                width="16"
                height="16"
              />
            </div>
            <UIIcon
              v-else-if="searchQuery"
              name="small-close"
              class="text-black-60 cursor-pointer ms-2"
              @click="clearSearch"
            />
          </UIBaseTransition>
        </div>

        <div
          v-else-if="contextualIcon === 'search'"
          class="cursor-pointer flex items-center justify-center w-10 h-10 rounded-[12px] bg-white border-base border-black-20"
          @click="handleContextualIconClick"
        >
          <UIIcon
            name="search"
            class="!size-6 text-black-100"
          />
        </div>

        <div
          v-else-if="contextualIcon === 'chevron-big-left'"
          class="cursor-pointer flex items-center justify-center w-10 h-10 rounded-[12px] border-2"
          :class="isOverlayDetailPage ? 'border-white/70' : 'border-black-60'"
          @click="handleContextualIconClick"
        >
          <UIIcon
            :name="contextualIcon"
            class="!size-6"
            :class="isOverlayDetailPage ? 'text-white' : 'text-black-100'"
          />
        </div>
        <UIIcon
          v-else
          :name="contextualIcon"
          class="cursor-pointer"
          :class="isOverlayDetailPage ? 'text-white' : ''"
          @click="handleContextualIconClick"
        />
      </div>

      <UILogo
        v-if="!isOverlayDetailPage"
        :to="'/'"
        :size="22"
        class="justify-self-center"
      />
      <div
        v-else
        class="justify-self-center"
      ></div>

      <div class="flex items-center gap-2 justify-self-end">
        <SitePreferencesControl />
        <UIIcon
          :name="isSidePanelOpen ? 'big-close' : 'menu'"
          class="cursor-pointer"
          :class="isOverlayDetailPage ? 'text-white' : ''"
          @click="toggleMenu"
        />
      </div>
    </nav>

    <div
      v-if="isPortfolioPage && isSearchExpanded"
      ref="portfolioSearchInputRef"
      class="w-full bg-gold-primary-10 px-5 py-3 portfolio-search-container"
    >
      <PortfolioSearch
        :hide-dropdown="true"
        container-class="w-full"
        input-class="text-sm"
      />
    </div>

    <MobileBanner
      v-if="!isOverlayDetailPage"
      :banner-config="currentBannerConfig"
    />
  </div>
</template>

<script setup lang="ts">
import { SIDE_PANELS } from '@/components/dynamic/maps';
import { onClickOutside, useDebounceFn } from '@vueuse/core';
import PortfolioSearch from '@/components/portfolio/PortfolioSearch.vue';
import SitePreferencesControl from '@/components/SitePreferencesControl.vue';
import { FILTERS } from '@/types/ui';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const { isDesktop } = useAppBreakpoints();

const isSearchExpanded = ref(false);
const searchInputRef = ref<HTMLInputElement>();
const searchContainerRef = ref<HTMLDivElement>();

const isPropertiesLoading = inject<Ref<boolean> | undefined>('isPropertiesLoading', undefined);
const filtersQuery = inject<ComputedRef<string> | undefined>('filtersQuery', undefined);
const clearSearchQuery = inject<(() => void) | undefined>('clearSearchQuery', undefined);

const searchQuery = ref((route.query.search as string) || filtersQuery?.value || '');

onMounted(() => {
  const urlSearch = route.query.search as string;
  const filterSearch = filtersQuery?.value;

  if (urlSearch) {
    searchQuery.value = urlSearch;
  } else if (filterSearch) {
    searchQuery.value = filterSearch;
  } else if (isExplorePage.value) {
    const { getFilter } = useFiltersStore();
    const storedFilters = getFilter(FILTERS.PROPERTIES) || {};
    if (storedFilters.prelaunchQuery) {
      searchQuery.value = storedFilters.prelaunchQuery;
    }
  }
});
const isSearchLoading = computed(() => {
  const routePath = route.path || '';
  const isHomePage = routePath === '/' || routePath === '/';
  const hasSearchQuery =
    !!route.query.search || !!searchQuery.value.trim() || !!(filtersQuery?.value && filtersQuery.value.trim());
  return isHomePage && (isPropertiesLoading?.value || false) && hasSearchQuery;
});

const portfolioSearch = usePortfolioSearch();

const portfolioSearchQuery = computed(() => portfolioSearch.searchQuery.value);

const portfolioSearchInputRef = ref<HTMLElement>();

onClickOutside(
  searchContainerRef,
  () => {
    if (isPortfolioPage.value) {
      if (isSearchExpanded.value && !portfolioSearchQuery.value.trim()) {
        isSearchExpanded.value = false;
        portfolioSearch.clearSearch();
      }
    } else {
      if (isSearchExpanded.value && !searchQuery.value.trim()) {
        isSearchExpanded.value = false;
      }
    }
  },
  { ignore: [portfolioSearchInputRef] },
);

watch(isSearchExpanded, (newValue) => {
  if (newValue && isPortfolioPage.value) {
    nextTick(() => {
      const input = portfolioSearchInputRef.value?.querySelector('input') as HTMLInputElement;
      input?.focus();
    });
  } else if (newValue) {
    nextTick(() => {
      searchInputRef.value?.focus();
    });
  }
});

watch(route, () => {
  if (isSearchExpanded.value) {
    isSearchExpanded.value = false;
    searchQuery.value = '';
    if (isPortfolioPage.value) {
      portfolioSearch.clearSearch();
    }
  }
});

watch(portfolioSearchQuery, (newQuery) => {
  if (isPortfolioPage.value && isSearchExpanded.value && !newQuery.trim()) {
    setTimeout(() => {
      if (
        !portfolioSearchQuery.value.trim() &&
        document.activeElement !== portfolioSearchInputRef.value?.querySelector('input')
      ) {
        isSearchExpanded.value = false;
      }
    }, 300);
  }
});

const { isDetailPage: checkIsDetailPage } = useRouteHelpers();
const isDetailPage = computed(() => checkIsDetailPage());

const isOverlayDetailPage = computed(() => {
  const p = route.path || '';
  // Transparent header for services and training detail pages
  return p.includes('/services/') || p.includes('/training/');
});

const isPortfolioPage = computed(() => false);

const isTrainingPage = computed(() => {
  const path = route.path || '';
  const slug = route.params?.slug;
  if (slug) return false;
  return path.includes('/developer-vault') || path.includes('/training');
});

const isTrainingDetailPage = computed(() => {
  const path = route.path || '';
  return path.includes('/training/') && !!route.params?.slug;
});

const isExplorePage = computed(() => {
  if (isDesktop.value) return false;
  const exploreQuery = route.query.explore;
  return exploreQuery === '1' || exploreQuery === 'true' || exploreQuery === 'map';
});

const contextualIcon = computed<'chevron-big-left' | 'search'>(() => {
  if (isPortfolioPage.value) {
    return 'search';
  }

  if (isTrainingDetailPage.value || isDetailPage.value) {
    return 'chevron-big-left';
  }

  return 'search';
});

const handleContextualIconClick = () => {
  if (isPortfolioPage.value) {
    handleSearchClick();
    return;
  }

  if (isTrainingDetailPage.value || isDetailPage.value) {
    router.back();
  } else {
    handleSearchClick();
  }
};

const handleSearchClick = () => {
  isSearchExpanded.value = true;
};

const handleClosePortfolioSearch = () => {
  isSearchExpanded.value = false;
  portfolioSearch.clearSearch();
};

const handleSearchBlur = () => {
  if (!searchQuery.value.trim()) {
    setTimeout(() => {
      isSearchExpanded.value = false;
    }, 150);
  }
};

const { performSearch: searchFunction, clearSearch: clearSearchFunction } = useSearchHelpers();

const trainingSearch = useTraining();

watch(
  () => isTrainingPage.value,
  (isTraining) => {
    if (isTraining) {
      searchQuery.value = trainingSearch.searchQuery.value;
    }
  },
  { immediate: true },
);

const updateTrainingSearch = (query: string) => {
  if (isTrainingPage.value) {
    trainingSearch.searchQuery.value = query;
  }
};

const debouncedUpdateTrainingSearch = useDebounceFn(updateTrainingSearch, 300);

const performAutoSearch = (query: string) => {
  if (isSearchExpanded.value && !isTrainingPage.value && !isPortfolioPage.value) {
    searchFunction(query, () => {});
  }
};

const debouncedAutoSearch = useDebounceFn(performAutoSearch, 400);

watch(searchQuery, (newQuery) => {
  if (isTrainingPage.value) {
    debouncedUpdateTrainingSearch(newQuery);
  } else if (!isPortfolioPage.value) {
    debouncedAutoSearch(newQuery);
  }
});

watch(
  () => filtersQuery?.value,
  (queryValue) => {
    if (!isTrainingPage.value) {
      const routePath = route.path || '';
      const isHomePage = routePath === '/' || routePath === '/';
      if (isHomePage) {
        searchQuery.value = queryValue || '';
      }
    }
  },
  { immediate: true },
);

watch(
  () => route.query.search,
  (urlSearch) => {
    if (urlSearch && typeof urlSearch === 'string') {
      searchQuery.value = urlSearch;
    }
  },
  { immediate: true },
);

const clearSearch = () => {
  if (isTrainingPage.value) {
    trainingSearch.searchQuery.value = '';
  } else if (isExplorePage.value) {
    searchQuery.value = '';

    const { getFilter, setFilter } = useFiltersStore();
    const filters = getFilter(FILTERS.PROPERTIES) || {};
    filters.query = '';
    filters.prelaunchQuery = '';
    setFilter(FILTERS.PROPERTIES, filters);

    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('explore-search', { detail: { query: '' } }));
    }
  } else {
    const routePath = route.path || '';
    const isHomePage = routePath === '/' || routePath === '/';
    if (isHomePage && clearSearchQuery) {
      searchQuery.value = '';

      clearSearchQuery();

      nextTick(() => {
        const newQuery = { ...route.query };
        delete newQuery.search;
        router.replace({ query: newQuery }).catch(() => {});
      });
    } else {
      clearSearchFunction(searchQuery, isSearchExpanded);
      searchQuery.value = '';
    }
  }
};

const performSearch = () => {
  searchFunction(searchQuery.value, () => {
    isSearchExpanded.value = false;
  });
};

const { getBannerConfig } = useMobileBanner();
const currentBannerConfig = computed(() => getBannerConfig());

const uiStore = useUIStore();
const showMenuSidePanel = () => {
  uiStore.showSidePanel(SIDE_PANELS.MENU, {}, {}, 'slideX');
};
const isSidePanelOpen = computed(() => uiStore.getDynamicSidePanel.value);

const closeSidePanel = () => {
  uiStore.cleanSidePanel();
};

const toggleMenu = () => {
  if (isSidePanelOpen.value) return closeSidePanel();
  showMenuSidePanel();
};
</script>

<style scoped></style>
